import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";

const SettingsContext = createContext(null);

const applyFavicon = (url) => {
  if (!url || typeof document === "undefined") return;
  let link = document.querySelector("link[rel='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = url;
};

/** Normalize URL for comparison (strip trailing slash, lowercase). */
const norm = (u) => (u || "").trim().toLowerCase().replace(/\/+$/, "") || "/";

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [allMenus, setAllMenus] = useState([]);

  const load = () => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      applyFavicon(r.data?.favicon_url);
      if (r.data?.site_name) document.title = `${r.data.site_name} — ${r.data.slogan || ""}`;
    }).catch(() => setSettings({}));
    api.get("/menus").then((r) => setAllMenus(r.data || [])).catch(() => setAllMenus([]));
  };

  useEffect(load, []);

  const value = useMemo(() => {
    const publishedMenus = allMenus
      .filter((m) => m.status === "published")
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const hiddenUrls = new Set(
      allMenus.filter((m) => m.status !== "published").map((m) => norm(m.url))
    );
    const isMenuHidden = (url) => hiddenUrls.has(norm(url));
    return {
      settings: settings || {},
      menus: publishedMenus,
      allMenus,
      isMenuHidden,
      refresh: load,
    };
  }, [settings, allMenus]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () =>
  useContext(SettingsContext) || {
    settings: {},
    menus: [],
    allMenus: [],
    isMenuHidden: () => false,
  };
